import React, { useState, useEffect } from 'react';
import { Button, Box, useTheme, Grid, Paper, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Check, Close, Autorenew, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useGetCurrentCompanyIndexQuery } from '../../../../features/users/usersApiSlice';
import { useGetGroupedCompanyOutputMutation, useConfirmCompanyDomainMutation, useRemoveCompanyDomainMutation } from '../../../../features/data_assets/dataAssetsApiSlice';
import useAuth from '../../../../hooks/useAuth';
import Header from '../../global/Header';

import { tokens } from '../../../../theme';

const CompanyModelQa = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [currentCompany, setCurrentCompany] = useState(null);
  const [changeLog, setChangeLog] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [openReassignDialog, setOpenReassignDialog] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [previewDomain, setPreviewDomain] = useState(null);
  const [dataGridKey, setDataGridKey] = useState(0);
  const [jumpToIndex, setJumpToIndex] = useState('');

  const { username } = useAuth();

  const [getGroupedCompanyOutput] = useGetGroupedCompanyOutputMutation();
  const [confirmCompanyDomain] = useConfirmCompanyDomainMutation();
  const [removeCompanyDomain] = useRemoveCompanyDomainMutation();
  const { data: userIndexData, isLoading: isUserIndexLoading } = useGetCurrentCompanyIndexQuery(username);

  console.log("Current User Index: ", userIndexData);

  useEffect(() => {
    if (!isUserIndexLoading && userIndexData?.currentCompanyIndex !== undefined) {
      setCurrentIndex(userIndexData.currentCompanyIndex);
      console.log("setting index: ", userIndexData.currentCompanyIndex);
      fetchCompany(userIndexData.currentCompanyIndex, 'right');
    }
  }, [isUserIndexLoading, userIndexData]);

  const fetchCompany = async (index, direction) => {
    try {
      const response = await getGroupedCompanyOutput({ username, currentIndex: index, direction }).unwrap();
      console.log("Company Response: ", response);
      setCurrentCompany(response.data);
      setChangeLog(response.changeLog);
      setCurrentIndex(response.currentIndex);
      setPreviewDomain(null); // Reset preview when navigating between companies
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  useEffect(() => {
    if (currentIndex !== null) {
      fetchCompany(currentIndex, 'right');
    }
  }, []);

  const handleConfirm = async (domain) => {
    try {
      await confirmCompanyDomain({
        username,
        companyDomain: domain.company_domain,
        companyId: currentCompany.FXF_COMPANY_ID
      });
      updateDomainStatus(domain.company_domain, 'confirmed');
    } catch (error) {
      console.error('Error confirming domain:', error);
    }
  };

  const handleRemove = async (domain) => {
    try {
      updateDomainStatus(domain.company_domain, 'removed');
      await removeCompanyDomain({
        username,
        companyDomain: domain.company_domain,
        companyId: currentCompany.FXF_COMPANY_ID
      });
    } catch (error) {
      console.error('Error removing domain:', error);
    }
  };

  const updateDomainStatus = (companyDomain, status) => {
    setChangeLog(prev => [
      ...prev,
      { COMPANY_DOMAIN: companyDomain, CHANGE_MADE: status, USERNAME: username, TIMESTAMP_UTC: new Date().toISOString() }
    ]);
  
    setCurrentCompany(prev => {
      const updatedDomains = prev.AGGREGATED_DOMAIN_DATA.map(domain =>
        domain.company_domain === companyDomain ? { ...domain, status, username, timestamp: new Date().toISOString() } : domain
      );
      return { ...prev, AGGREGATED_DOMAIN_DATA: updatedDomains };
    });
  
    // Force re-render of DataGrid by updating key
    console.log("Domain: ", companyDomain, " Status: ", status, " Username: ", username, " Timestamp: ", new Date().toISOString());
    setDataGridKey(prevKey => prevKey + 1);
  };
  
  const handleReassign = (domain) => {
    setSelectedDomain(domain);
    setOpenReassignDialog(true);
  };

  const handleReassignConfirm = (newCompany) => {
    // Handle reassign domain to newCompany
    setOpenReassignDialog(false);
  };

  const handlePreviousCompany = () => {
    fetchCompany(currentIndex, 'left');
  };

  const handleNextCompany = () => {
    fetchCompany(currentIndex, 'right');
  };

  const handleDomainClick = (domain) => {
    setPreviewDomain(domain);
  };

  const handleJumpToIndexChange = (e) => {
    setJumpToIndex(e.target.value);
  };

  const handleJumpToIndexSubmit = (e) => {
    e.preventDefault();
    if (jumpToIndex) {
      fetchCompany(parseInt(jumpToIndex-1, 10), 'right');
    }
  };

  const mergeDomainData = () => {
    if (!currentCompany) return [];

    const domains = currentCompany.AGGREGATED_DOMAIN_DATA.map(domain => {
      const change = changeLog.find(log => log.COMPANY_DOMAIN === domain.company_domain);
      return {
        ...domain,
        status: change ? change.CHANGE_MADE : 'existing',
        username: change ? change.USERNAME : '',
        timestamp: change ? change.TIMESTAMP_UTC : ''
      };
    });

    return domains;
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title="Company QA" subtitle="Click through the companies below and confirm, remove, or reassign the domains to other companies." />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={7}>
          <Paper>
            <Box p={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper variant="outlined" style={{ padding: '10px' }}>
                    <Typography variant="h6">Company ID</Typography>
                    <Typography variant="body1">{currentCompany?.FXF_COMPANY_ID}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" style={{ padding: '10px' }}>
                    <Typography variant="h6">Current Index</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{currentIndex}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: '10px' }}>
                    <Typography variant="h6">Company Name</Typography>
                    <Typography variant="body1">{currentCompany?.FXF_COMPANY_NAME}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: '10px' }}>
                    <Typography variant="h6">Company Domain</Typography>
                    <Typography variant="body1">{currentCompany?.FXF_COMPANY_DOMAIN}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: '10px' }}>
                    <Typography variant="h6">Total Occurrences</Typography>
                    <Typography variant="body1">{currentCompany?.TOTAL_COMPANY_OCCURANCES}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <form onSubmit={handleJumpToIndexSubmit}>
                    <TextField
                      label="Jump to Index"
                      variant="outlined"
                      value={jumpToIndex}
                      onChange={handleJumpToIndexChange}
                      sx={{ marginRight: 2 }}
                    />
                    <Button type="submit" variant="contained" color="primary">
                      Jump
                    </Button>
                  </form>
                </Grid>
              </Grid>
              <DataGrid
                key={dataGridKey}
                rows={mergeDomainData()}
                columns={[
                  { field: 'company_domain', headerName: 'Domain', width: 180 },
                  { field: 'company_name', headerName: 'Company Name', width: 280 },
                  { field: 'total_occurances', headerName: 'Occurrences', width: 100 },
                  { field: 'username', headerName: 'User', width: 120 },
                  { field: 'timestamp', headerName: 'Timestamp', width: 100 },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    width: 150,
                    renderCell: (params) => (
                      <>
                        <IconButton color="success" onClick={() => handleConfirm(params.row)} disabled={params.row.status !== 'existing'}>
                          <Check />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleRemove(params.row)} disabled={params.row.status !== 'existing'}>
                          <Close />
                        </IconButton>
                        {
                        //<IconButton color="secondary" onClick={() => handleReassign(params.row)} disabled={params.row.status !== 'existing'}>
                        //  <Autorenew />
                        //</IconButton>
                        }
                      </>
                    ),
                  },
                ]}
                getRowId={(row) => row.company_domain}
                autoHeight
                onRowClick={(params) => handleDomainClick(params.row)}
                sx={{
                  '& .MuiDataGrid-row.removed-domain': {
                    backgroundColor: colors.redAccent[300],
                    '& .MuiDataGrid-cell': {
                      color: 'black',
                      fontWeight: 'bold'
                    }
                  },
                  '& .MuiDataGrid-row.confirmed-domain': {
                    backgroundColor: colors.greenAccent[300],
                    '& .MuiDataGrid-cell': {
                      color: 'black',
                      fontWeight: 'bold'
                    }
                  },
                }}
                getRowClassName={(params) => {
                  const change = changeLog.find(log => log.COMPANY_DOMAIN === params.row.company_domain);
                  //console.log('change:', change);
                  if (change) {
                    //console.log('domain and change:', change, ' ', params.row.company_domain);
                    return change.CHANGE_MADE === 'confirmed' ? 'confirmed-domain' : 'removed-domain';
                  }
                  return '';
                }}
              />
              <Box display="flex" justifyContent="space-between" mt={2}>
                <IconButton onClick={handlePreviousCompany}>
                  <ArrowBack />
                </IconButton>
                <IconButton onClick={handleNextCompany}>
                  <ArrowForward />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={5}>
          <Paper>
            <Box p={2}>
              <Typography variant="h6">Domain Preview</Typography>
              {previewDomain && (
                <Box mt={2}>
                  <iframe
                    src={`https://${previewDomain.company_domain}`}
                    title="Domain Preview"
                    width="100%"
                    height="400px"
                    style={{ border: 'none' }}
                  />
                  <Box mt={2}>
                    <Typography variant="body1"><strong>Domain:</strong> {previewDomain.company_domain}</Typography>
                    <Typography variant="body1"><strong>Company Name:</strong> {previewDomain.company_name}</Typography>
                    <Typography variant="body1"><strong>Occurrences:</strong> {previewDomain.total_occurances}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openReassignDialog} onClose={() => setOpenReassignDialog(false)}>
        <DialogTitle>Reassign Domain</DialogTitle>
        <DialogContent>
          <TextField label="Search Companies" fullWidth />
          {/* Add search results here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReassignDialog(false)}>Cancel</Button>
          <Button onClick={() => handleReassignConfirm(selectedDomain)}>Reassign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyModelQa;
